'use client';

import { useEffect, useState } from 'react';

import { Cms } from '@bytel/product-wall';
import { Container, Section } from '@bytel/trilogy-react';

import { cmsService } from '@services/cms';

import { AnimcoBannerType } from '@app-types/animco';
import { HomePackCategory, HomePackResponseWithCategories, PACK_HASH, StaticPackProduct } from '@app-types/home-pack';
import { LegalType } from '@app-types/legals';
import { FaiType, OffersByCategoryCode } from '@app-types/plan';

import { AnimcoBanner } from '@components/animco-banner';
import { Faq } from '@components/faq';
import { Legals } from '@components/legals';
import { LetterVariant } from '@components/letter-variant';
import { SSIBlock } from '@components/ssi-block';
import { TriggerLogin } from '@components/trigger-login';

import { partials } from '@helpers/partials';
import { noop } from '@helpers/try-catch';

import { PackConfigType } from '@home-pack/helpers';

import { Breadcrumb } from './breadcrumb';
import { PackBody } from './pack-body';

export type PackProps = {
    defaultPlans: FaiType[];
    config: { [index: string]: PackConfigType };
    staticsCategories?: HomePackResponseWithCategories;
    currentStaticCategory?: HomePackCategory;
    faqQuestionList?: {
        question: string;
        answer: string;
        id: number;
    }[];
    trackingData: {
        page: string;
        marketLine: string;
    };
    offersByCategory: OffersByCategoryCode;
    banner?: AnimcoBannerType;
};

export function Pack({
    defaultPlans,
    config,
    staticsCategories,
    currentStaticCategory,
    trackingData,
    offersByCategory,
    banner,
}: PackProps) {
    const [selectedStaticProduct, setSelectedStaticProduct] = useState<StaticPackProduct | null>({
        mainProduct: null,
        details: null,
    });
    const [productDetailsCms, setProductDetailsCms] = useState<string>();
    const [timelineDetailsCms, setTimelineDetailsCms] = useState<string>();
    const [currentCategory, setCurrentCategory] = useState(currentStaticCategory);

    let currentCategorySlug = currentCategory?.slug as PACK_HASH;
    let faqUrl = config[currentCategorySlug]?.faqCeUrl;
    let specificLegals = config[currentCategorySlug]?.legalNoticesCeUrl;
    let timelineUrl = config[currentCategorySlug]?.timelineToShow ?? '';

    const setSlug = (slug: string) => {
        setCurrentCategory(
            staticsCategories && staticsCategories.categoryProductsSamsung?.find((category) => category.slug === slug),
        );
    };

    const setCmsData = (url: string, setter: (val: string) => void) => {
        cmsService
            .getPartialsCms(url)
            .then((data) => {
                if (data) {
                    setter(data);
                }
            })
            .catch(noop);
    };

    useEffect(() => {
        currentCategorySlug = currentCategory?.slug as PACK_HASH;
        faqUrl = config[currentCategorySlug]?.faqCeUrl;
        specificLegals = config[currentCategorySlug]?.legalNoticesCeUrl;
        if (currentCategory?.productsSamsung) {
            setCmsData(`combo/${currentCategory.productsSamsung[0]?.partialsUrlKey}`, setProductDetailsCms);
        }
        timelineUrl = config[currentCategorySlug]?.timelineToShow ?? '';
        setCmsData(timelineUrl, setTimelineDetailsCms);
    }, [currentCategory]);

    useEffect(() => {
        if (selectedStaticProduct?.mainProduct?.partialsUrlKey) {
            setCmsData(`combo/${selectedStaticProduct?.mainProduct?.partialsUrlKey}`, setProductDetailsCms);
        }
    }, [selectedStaticProduct]);

    return (
        <>
            <AnimcoBanner banner={banner} />
            <PackBody
                defaultPlans={defaultPlans}
                config={config}
                staticsCategories={staticsCategories}
                currentStaticCategory={currentCategory}
                setSelectedStaticProduct={setSelectedStaticProduct}
                selectedStaticProduct={selectedStaticProduct}
                trackingData={trackingData}
                offersByCategory={offersByCategory}
                setSlug={setSlug}
                timelineDetailsCms={timelineDetailsCms}
            />
            <SSIBlock tagId='fai-contenu' url={partials.MARKETING.FAI_CONTENT} />
            <Cms content={productDetailsCms} />
            <SSIBlock tagId='fai-engagements' url={partials.MARKETING.FAI_COMMITMENTS} />
            {faqUrl ? (
                <Section>
                    <Container>
                        <Faq
                            id='faqPack'
                            url={faqUrl}
                            title={
                                <>
                                    Des questi
                                    <LetterVariant letter='o' />
                                    ns sur nos box + high-tech ?<br /> C&apos;est par ici
                                </>
                            }
                            titleAlt="Des questions sur nos box + high-tech ? C'est par ici"
                        />
                    </Container>
                </Section>
            ) : null}
            <Section>
                <Container>
                    <Breadcrumb slug={currentCategorySlug} config={config} />
                    <Legals id='legals-pack' url={specificLegals || LegalType.PACK} />
                </Container>
            </Section>
            <TriggerLogin />
        </>
    );
}
